import { useState } from "react";
import { Activity, ChartCandlestick, ExternalLink, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LaunchBadge } from "@/components/LaunchBadge";
import { useMarketChart } from "./useMarketChart";
import { CandleCanvas } from "./CandleCanvas";
import { ChartLegend, candleTime } from "./ChartLegend";
import "@/styles/market-chart.css";

const SITE = process.env.REACT_APP_GECKOTERMINAL_SITE_URL;
const TRADINGVIEW = process.env.REACT_APP_TRADINGVIEW_URL;
if (!SITE || !TRADINGVIEW) throw new Error("Missing chart source URLs");
const STATES = {
  loading: ["CONNECTING TO MARKET DATA", "GeckoTerminal · Solana"],
  no_pool: ["AWAITING MARKET DATA", "No indexed trading pool was found for this token address."],
  no_data: ["NO CANDLES AVAILABLE", "A pool was found, but no trading history is available for this interval."],
  unavailable: ["CHART TEMPORARILY UNAVAILABLE", "The market-data source could not be reached. No estimated data is shown."],
};

export const MarketChart = ({ placement }) => {
  const prefix = `${placement}-market`;
  const [interval, setInterval] = useState("1m");
  const [inspected, setInspected] = useState(null);
  const { data, isPending, isError, isFetching, refetch } = useMarketChart(interval);
  const status = isPending ? "loading" : isError ? "unavailable" : data?.status || "unavailable";
  const candles = status === "ok" ? data.candles : [];
  const latest = candles[candles.length - 1];
  const sourceUrl = data?.pool_address ? `${SITE.replace(/\/$/, "")}/solana/pools/${encodeURIComponent(data.pool_address)}` : SITE;

  return <section className={`market-chart market-chart--${placement}`} aria-labelledby={`${prefix}-heading`} data-testid={`${prefix}-chart`} data-status={status}>
    <header className="market-chart-heading">
      <div><p className="market-eyebrow" data-testid={`${prefix}-eyebrow`}>SOLANA / MARKET OBSERVATORY</p><h2 id={`${prefix}-heading`} data-testid={`${prefix}-heading`}><ChartCandlestick aria-hidden="true" /> $KBULL / USD</h2></div>
      <LaunchBadge testId={`${prefix}-launch-status`} />
    </header>
    <div className="market-chart-tool">
      <div className="market-toolbar">
        <span className="market-source" data-testid={`${prefix}-source`}><Activity aria-hidden="true" /> GECKOTERMINAL</span>
        <div className="market-controls">
          <div className="market-intervals" role="group" aria-label="Candle interval" data-testid={`${prefix}-intervals`}>
            {["1m", "5m", "1h", "1d"].map((value) => <Button variant="ghost" key={value} aria-pressed={interval === value} data-testid={`${prefix}-interval-${value}`} onClick={() => { setInspected(null); setInterval(value); }}>{value.toUpperCase()}</Button>)}
          </div>
          <Button variant="ghost" size="icon" disabled={isFetching} title="Refresh market data" aria-label="Refresh market data" data-testid={`${prefix}-refresh`} onClick={() => refetch()}><RefreshCw aria-hidden="true" className={isFetching ? "market-refreshing" : ""} /></Button>
        </div>
      </div>
      <ChartLegend candle={status === "ok" ? inspected || latest : null} prefix={prefix} />
      {status === "ok" ? <CandleCanvas key={interval} candles={candles} prefix={prefix} onInspect={setInspected} /> : <div className="market-empty" role="status" aria-live="polite" data-testid={`${prefix}-state`}>
        <ChartCandlestick aria-hidden="true" /><strong data-testid={`${prefix}-state-title`}>{STATES[status]?.[0]}</strong><p data-testid={`${prefix}-state-message`}>{STATES[status]?.[1]}</p>
        {status === "unavailable" && <Button variant="outline" disabled={isFetching} data-testid={`${prefix}-retry`} onClick={() => refetch()}><RefreshCw aria-hidden="true" /> RETRY</Button>}
      </div>}
      <div className="market-chart-meta">
        <span data-testid={`${prefix}-pool-name`}>{data?.pool_name ? `SOURCE PAIR: ${data.pool_name}` : "SOLANA · OHLCV"}</span>
        <span data-testid={`${prefix}-latest-candle`}>{latest ? `LATEST CANDLE: ${candleTime(latest.time)}` : "NO SYNTHETIC CANDLES"}</span>
      </div>
    </div>
    <footer className="market-chart-footer">
      <p data-testid={`${prefix}-notice`}>Market data is independent of the site's AWAITING LAUNCH status.</p>
      <div><a href={sourceUrl} target="_blank" rel="noopener noreferrer" data-testid={`${prefix}-source-link`}>GeckoTerminal <ExternalLink aria-hidden="true" /></a><a href={TRADINGVIEW} target="_blank" rel="noopener noreferrer" data-testid={`${prefix}-attribution`}>Charts by TradingView <ExternalLink aria-hidden="true" /></a></div>
      {data?.fetched_at && !isError && <span data-testid={`${prefix}-fetched-at`}>FETCHED {new Date(data.fetched_at).toLocaleString("en-GB", { timeZone: "UTC" })} UTC</span>}
    </footer>
  </section>;
};